import { useAppForm } from "@/components/Forms";
import {
  CarType,
  DeliveryOptionSelection,
  type Doc,
  type SelectedOption,
} from "@/types/models/delivery.types";
import { deliveryFormOpts } from "./form-config";

const __inferFormType = () => useAppForm({ ...deliveryFormOpts });
export type DeliveryFormType = ReturnType<typeof __inferFormType>;

// ─── Address ────────────────────────────────────────────────────────────────
export interface AddressValues {
  latitude: number;
  longitude: number;
  city: string;
  address: string;
  postalCode: string;
  place_id: string;
  country: string;
  addressPrecision?: string;
}

export const ADDRESS_DEFAULTS: AddressValues = {
  latitude: 0,
  longitude: 0,
  city: "",
  address: "",
  postalCode: "",
  place_id: "",
  country: "",
  addressPrecision: "",
};
export interface SummaryData {
  id: number | undefined;
  type: string;
  clientFirstName: string;
  clientLastName: string;
  clientPhone: string;
  clientEmail: string;
  clientCompany: string;
  clientSiret: string;
  clientDrivingLicenceNumber: string;
  representativeFirstName: string;
  representativeLastName: string;
  representativePhone: string;
  representativeEmail: string;
  representativeCompany: string;
  representativeSiret: string;
  carAvailabilityDate: string;
  carPickUpDate: string;
  carDeliveryDeadline: string;
  fromAddress: AddressValues;
  toAddress: AddressValues;
  toReturnFromAddress: AddressValues;
  toReturnToAddress: AddressValues;
  vehicles: Array<{
    vin: string;
    vehicleCode: string;
    marque: string;
    model: string;
    type: string;
    carType: CarType | "";
  }>;
  category: string;
  comment: string;
  selectedOptions: DeliveryOptionSelection[];
  organization: { slug: string };
}
// ─── Vehicle ────────────────────────────────────────────────────────────────
export interface VehicleValues {
  id?: number;
  carType: CarType | ""; // A | B | C
  type: string; // GAS | HYBRID | ELECTRIC
  vin: string;
  vehicleCode: string;
  marque: string;
  model: string;
  toRetrieve?: boolean;
}

export const VEHICLE_DEFAULTS: VehicleValues = {
  carType: "A",
  type: "",
  vin: "",
  vehicleCode: "",
  marque: "",
  model: "",
};

// ─── Form Field Prefixes ────────────────────────────────────────────────────
export type AddressPrefix =
  | "step1.fromAddress"
  | "step1.toAddress"
  | "step2.toReturnFromAddress"
  | "step2.toReturnToAddress";

export type VehiclePrefix = "step1.vehicle" | "step2.returnVehicle";

// ─── Form Values (per-step groups for FormGroup) ────────────────────────────
export interface Step0Values {
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  clientSiret: string;
  clientDrivingLicenceNumber: string;
  selectedMandataireId: string;
  selectedOrganizationId: string;
  representativeFirstName: string;
  representativeLastName: string;
  representativeEmail: string;
  representativePhone: string;
  representativeCompany: string;
  representativeSiret?: string;
  carAvailabilityDate: string;
  carPickUpDate: string;
  carDeliveryDeadline: string;
}

export interface Step1Values {
  fromAddress: AddressValues;
  toAddress: AddressValues;
  vehicle: VehicleValues;
}

export interface Step2Values {
  toReturnFromAddress: AddressValues;
  toReturnToAddress: AddressValues;
  returnVehicle: VehicleValues;
}

export interface LastStepValues {
  category: string;
  selectedOptions: { selected: SelectedOption[] };
  comment: string;
  docs?: Doc[];
}

export interface DeliveryFormValues {
  step0: Step0Values;
  step1: Step1Values;
  step2: Step2Values;
  lastStep: LastStepValues;
}

// ─── Submit Meta ────────────────────────────────────────────────────────────
export type SubmitAction = "create" | "update";

export interface SubmitMeta {
  submitAction: SubmitAction;
}

// ─── Component Props ────────────────────────────────────────────────────────
export interface DeliveryFormProps {
  type: string;
  formId: string;
  data?: import("@/libs/generated/operations").FindDeliveryByIdQuery["findDeliveryById"];
  title: string;
  initialStep?: number;
  onSubmit: (data: DeliveryFormValues) => void;
}
